import { useState } from 'react';
import {
  Plus,
  Search,
  Filter,
  CreditCard,
  RefreshCw,
  XCircle,
  X,
} from 'lucide-react';
import {
  useSubscriptions,
  useCreateSubscription,
  useCancelSubscription,
  useRenewSubscription,
  type SubscriptionFilters,
  type Subscription,
  type SubscriptionStatus,
} from '../hooks/useSubscriptions';
import { Badge } from '../components/shared/Badge';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface SubscriptionsViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const statusLabels: Record<SubscriptionStatus, { label: string; color: string }> = {
  active: { label: 'Active', color: 'green' },
  pending: { label: 'Pending', color: 'blue' },
  cancelled: { label: 'Cancelled', color: 'red' },
  expired: { label: 'Expired', color: 'gray' },
  lapsed: { label: 'Lapsed', color: 'orange' },
};

const paymentStatusLabels: Record<string, { label: string; color: string }> = {
  current: { label: 'Current', color: 'green' },
  overdue: { label: 'Overdue', color: 'red' },
  grace_period: { label: 'Grace Period', color: 'orange' },
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function SubscriptionsView({ onNavigate }: SubscriptionsViewProps) {
  const [statusFilter, setStatusFilter] = useState<SubscriptionStatus | ''>('');
  const [searchText, setSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    policyNumber: '',
    dealId: '',
    organizationId: '',
    productId: '',
    premium: '',
    effectiveDate: '',
    expirationDate: '',
    paymentFrequency: 'annual' as string,
  });

  const filters: SubscriptionFilters = {
    status: statusFilter || undefined,
    search: searchText || undefined,
    limit: 100,
  };

  const { data, isLoading, error } = useSubscriptions(filters);
  const createSubscription = useCreateSubscription();
  const cancelSubscription = useCancelSubscription();
  const renewSubscription = useRenewSubscription();

  const subscriptions = data?.subscriptions ?? [];

  function handleCreate() {
    createSubscription.mutate(
      {
        policyNumber: formData.policyNumber,
        dealId: formData.dealId || undefined,
        organizationId: formData.organizationId || undefined,
        productId: formData.productId || undefined,
        premium: Number(formData.premium) || 0,
        effectiveDate: formData.effectiveDate,
        expirationDate: formData.expirationDate,
        paymentFrequency: (formData.paymentFrequency as Subscription['paymentFrequency']) || undefined,
      },
      {
        onSuccess: () => {
          setShowForm(false);
          setFormData({ policyNumber: '', dealId: '', organizationId: '', productId: '', premium: '', effectiveDate: '', expirationDate: '', paymentFrequency: 'annual' });
        },
      },
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Subscriptions</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data ? `${data.total} subscription${data.total !== 1 ? 's' : ''}` : 'Policy subscriptions'}
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4" />
          New Subscription
        </button>
      </div>

      {/* Filter Bar */}
      <div className="border-b border-surface-200 bg-white px-6 py-3">
        <div className="flex items-center gap-3">
          <div className="flex rounded-lg border border-surface-200 p-0.5">
            {(['' as const, 'active', 'pending', 'cancelled', 'expired'] as const).map((s) => (
              <button
                key={s}
                className={cn(
                  'rounded-md px-3 py-1 text-sm font-medium transition-colors',
                  statusFilter === s ? 'bg-brand-50 text-brand-700' : 'text-slate-600 hover:text-slate-900',
                )}
                onClick={() => setStatusFilter(s)}
              >
                {s ? statusLabels[s].label : 'All'}
              </button>
            ))}
          </div>

          <form onSubmit={(e) => e.preventDefault()} className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input type="text" value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="Search subscriptions..." className="input pl-9" />
          </form>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {error ? (
          <div className="card p-8 text-center text-sm text-red-600">Failed to load subscriptions.</div>
        ) : isLoading ? (
          <div className="card">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 border-b border-surface-100 px-4 py-3 last:border-0">
                <div className="h-4 w-48 animate-pulse rounded bg-surface-200" />
                <div className="flex-1" />
                <div className="h-4 w-20 animate-pulse rounded bg-surface-200" />
              </div>
            ))}
          </div>
        ) : subscriptions.length > 0 ? (
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-200 bg-surface-50">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Policy #</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Organization</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Product</th>
                  <th className="px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Premium</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Effective</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Expiration</th>
                  <th className="px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Payment</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-100">
                {subscriptions.map((sub) => {
                  const sCfg = statusLabels[sub.status];
                  const pCfg = sub.paymentStatus ? paymentStatusLabels[sub.paymentStatus] : null;
                  return (
                    <tr key={sub.id} className="hover:bg-surface-50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{sub.policyNumber}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{sub.organizationName ?? '--'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{sub.productName ?? '--'}</td>
                      <td className="px-4 py-3 text-center"><Badge color={sCfg.color} size="sm">{sCfg.label}</Badge></td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-slate-900">{formatCurrency(sub.premium)}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{new Date(sub.effectiveDate).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{new Date(sub.expirationDate).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-center">
                        {pCfg ? <Badge color={pCfg.color} size="sm">{pCfg.label}</Badge> : <span className="text-xs text-slate-400">--</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {sub.status === 'active' && (
                            <button className="btn-secondary btn-sm text-red-600" onClick={() => cancelSubscription.mutate({ id: sub.id })} disabled={cancelSubscription.isPending} title="Cancel">
                              <XCircle className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {(sub.status === 'cancelled' || sub.status === 'expired') && (
                            <button className="btn-secondary btn-sm" onClick={() => renewSubscription.mutate(sub.id)} disabled={renewSubscription.isPending} title="Renew">
                              <RefreshCw className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            icon={CreditCard}
            title="No subscriptions yet"
            description="Create a subscription to track policies."
            actionLabel="New Subscription"
            onAction={() => setShowForm(true)}
          />
        )}
      </div>

      {/* Create Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="card w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">New Subscription</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="label mb-1">Policy Number</label>
                <input className="input" value={formData.policyNumber} onChange={(e) => setFormData((f) => ({ ...f, policyNumber: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label mb-1">Deal ID</label>
                  <input className="input" value={formData.dealId} onChange={(e) => setFormData((f) => ({ ...f, dealId: e.target.value }))} />
                </div>
                <div>
                  <label className="label mb-1">Organization ID</label>
                  <input className="input" value={formData.organizationId} onChange={(e) => setFormData((f) => ({ ...f, organizationId: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="label mb-1">Premium</label>
                <input className="input" type="number" value={formData.premium} onChange={(e) => setFormData((f) => ({ ...f, premium: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label mb-1">Effective Date</label>
                  <input className="input" type="date" value={formData.effectiveDate} onChange={(e) => setFormData((f) => ({ ...f, effectiveDate: e.target.value }))} />
                </div>
                <div>
                  <label className="label mb-1">Expiration Date</label>
                  <input className="input" type="date" value={formData.expirationDate} onChange={(e) => setFormData((f) => ({ ...f, expirationDate: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="label mb-1">Payment Frequency</label>
                <select className="input" value={formData.paymentFrequency} onChange={(e) => setFormData((f) => ({ ...f, paymentFrequency: e.target.value }))}>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="semi_annual">Semi-Annual</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleCreate} disabled={createSubscription.isPending || !formData.policyNumber}>
                {createSubscription.isPending ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
